import React, { useEffect, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import Footer from "../../CoreComponents/Footer";
import Header from "../../CoreComponents/Header";
import MainHeader from "../../CoreComponents/MainHeader";
import NavBar from "../../CoreComponents/NavBar";
import { is_franchise_admin, is_franchise_manager, is_superuser } from "../../Functionalities/Config";
import { canSettle } from "../Settlements/access";
import { ManageRestaurant, showMessage } from "../../Functionalities/Functions";
import { fmtDate, rupees } from "../Insights/format";
import { PAGE_SIZE, deletePayout, loadPayoutOptions, loadPayouts } from "./Logic";
import PayoutFormModal from "./PayoutFormModal";

export const canManagePayouts = () => Boolean(is_superuser || is_franchise_admin || is_franchise_manager);

const listOf = (result) => {
  if (!result || !result.data) return [];
  const rows = Array.isArray(result.data) ? result.data : result.data.results;
  return Array.isArray(rows) ? rows : [];
};

const EMPTY_FILTERS = { branch: "", from: "", to: "" };

export default function Payouts() {
  const [pending, setPending] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS, offset: 0 });
  const [page, setPage] = useState({ count: 0, results: [] });
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [options, setOptions] = useState({ transaction_types: [], transaction_modes: [] });
  const [editing, setEditing] = useState(null);
  const allowed = canSettle();

  useEffect(() => {
    if (!allowed) return;
    ManageRestaurant("", "", "get", "page_wise=false&for_dropdown=1&order_by_name=true").then((r) => setBranches(listOf(r)));
    loadPayoutOptions().then((body) => {
      if (body && body.Status === true) {
        setOptions({ transaction_types: body.transaction_types, transaction_modes: body.transaction_modes });
      }
    });
  }, [allowed]);

  const refresh = async (nextFilters = filters) => {
    setLoading(true);
    const body = await loadPayouts(nextFilters);
    setLoading(false);
    if (body && Array.isArray(body.results)) {
      setPage({ count: body.count, results: body.results });
      return;
    }
    setPage({ count: 0, results: [] });
    showMessage((body && body.Error) || "Could not load payouts", "danger", "Error!", "top", "top-right");
  };

  useEffect(() => {
    if (allowed) refresh(filters);
  }, [filters, allowed]);

  const apply = (event) => {
    event.preventDefault();
    setFilters({ ...pending, offset: 0 });
  };

  const confirmDelete = (payout) =>
    confirmAlert({
      title: "Delete this payout?",
      message: `${rupees(payout.amount)} to ${payout.branch_name} on ${fmtDate(payout.paid_on)}. The restaurant will no longer see it.`,
      buttons: [
        {
          label: "Delete",
          onClick: async () => {
            const body = await deletePayout(payout.id);
            if (body && body.Status === true) {
              showMessage("Payout deleted", "success", "Deleted", "top", "top-right");
              refresh();
            } else {
              showMessage((body && body.Error) || "Could not delete the payout", "danger", "Error!", "top", "top-right");
            }
          },
        },
        { label: "Cancel" },
      ],
    });

  const firstRow = page.count === 0 ? 0 : filters.offset + 1;
  const lastRow = Math.min(filters.offset + PAGE_SIZE, page.count);

  return (
    <div id="main-wrapper">
      <MainHeader />
      <Header name={"Restaurant payouts"} />
      <NavBar />

      <div className="content-body" style={{ minHeight: "100vh" }}>
        <div className="container-fluid">
          {!allowed ? (
            <div className="card">
              <div className="card-body">Only superusers and franchise admins can manage restaurant payouts.</div>
            </div>
          ) : (
            <div className="card">
              <div className="card-header d-flex flex-wrap align-items-center justify-content-between">
                <div>
                  <h4 className="card-title mb-1">Payouts to restaurants</h4>
                  <p className="mb-0 text-muted">Restaurants see each payout in their app, without a total.</p>
                </div>
                <button type="button" className="btn btn-primary" onClick={() => setEditing({})}>
                  + Record payout
                </button>
              </div>

              <div className="card-body">
                <form className="row align-items-end mb-3" onSubmit={apply}>
                  <div className="col-md-4 mb-2">
                    <label className="text-black font-w500">Restaurant</label>
                    <select
                      className="form-control"
                      value={pending.branch}
                      onChange={(event) => setPending((prev) => ({ ...prev, branch: event.target.value }))}
                    >
                      <option value="">All restaurants</option>
                      {branches.map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3 mb-2">
                    <label className="text-black font-w500">Paid from</label>
                    <input
                      className="form-control"
                      type="date"
                      value={pending.from}
                      onChange={(event) => setPending((prev) => ({ ...prev, from: event.target.value }))}
                    />
                  </div>
                  <div className="col-md-3 mb-2">
                    <label className="text-black font-w500">Paid to</label>
                    <input
                      className="form-control"
                      type="date"
                      value={pending.to}
                      onChange={(event) => setPending((prev) => ({ ...prev, to: event.target.value }))}
                    />
                  </div>
                  <div className="col-md-2 mb-2">
                    <button type="submit" className="btn btn-outline-primary btn-block">
                      Apply
                    </button>
                  </div>
                </form>

                <div className="table-responsive">
                  <table className="table table-sm dataTablesCard">
                    <thead>
                      <tr>
                        <th>Paid on</th>
                        <th>Restaurant</th>
                        <th className="text-right">Amount</th>
                        <th>Type</th>
                        <th>Mode</th>
                        <th>Transaction ID</th>
                        <th>Notes</th>
                        <th>Recorded by</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={9} className="text-center py-4">
                            Loading…
                          </td>
                        </tr>
                      ) : page.results.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="text-center py-4 text-muted">
                            No payouts recorded for these filters yet.
                          </td>
                        </tr>
                      ) : (
                        page.results.map((payout) => (
                          <tr key={payout.id}>
                            <td style={{ whiteSpace: "nowrap" }}>{fmtDate(payout.paid_on)}</td>
                            <td>{payout.branch_name}</td>
                            <td className="text-right font-w600" style={{ whiteSpace: "nowrap" }}>
                              {rupees(payout.amount)}
                            </td>
                            <td>{payout.transaction_type_label}</td>
                            <td>{payout.transaction_mode_label || "—"}</td>
                            <td>{payout.transaction_id || "—"}</td>
                            <td style={{ maxWidth: 260, whiteSpace: "pre-wrap" }}>{payout.notes || "—"}</td>
                            <td>{payout.created_by_name || "—"}</td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              <button type="button" className="btn btn-xs btn-outline-primary mr-1" onClick={() => setEditing(payout)}>
                                Edit
                              </button>
                              <button type="button" className="btn btn-xs btn-outline-danger" onClick={() => confirmDelete(payout)}>
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex align-items-center justify-content-between mt-2">
                  <span className="text-muted">
                    {page.count === 0 ? "No payouts" : `Showing ${firstRow}–${lastRow} of ${page.count}`}
                  </span>
                  <div>
                    <button
                      type="button"
                      className="btn btn-light btn-sm mr-2"
                      disabled={filters.offset === 0 || loading}
                      onClick={() => setFilters((prev) => ({ ...prev, offset: Math.max(0, prev.offset - PAGE_SIZE) }))}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      className="btn btn-light btn-sm"
                      disabled={lastRow >= page.count || loading}
                      onClick={() => setFilters((prev) => ({ ...prev, offset: prev.offset + PAGE_SIZE }))}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {editing !== null && (
        <PayoutFormModal
          payout={editing}
          branches={branches}
          options={options}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}

      <Footer />
    </div>
  );
}
